import { ForwardRefRenderFunction, forwardRef } from "react";
//#region types
import { FormHandles } from "models/propTypes/genericFormProps";
import { WintSuperkollAPIModel as ApiModel, WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { MissingDocumentFormTypes } from "models/formTypes";
import { MissingDocumentTodoSelectionFormViewProps } from "models/propTypes/missingDocumentPropTypes";
//#endregion
//#region components
import { useWintExpandableStep, useWintForm, WintForm } from "components/common/wintForms";
import Grid from "@mui/material/Grid";
import TodoFileUploaderSection from "./sections/fileUploaderSection";
import TodoPostWithoutPictureSection from "./sections/postWithoutPictureSection";
import TodoDocumentTypeSelectionStep from "./sections/documentTypeSelectionStep";
import TodoReceiptTypeSection from "./sections/receiptSection";
import TodoIncomingInvoiceTypeSection from "./sections/incomingInvoiceSection";
import TodoOtherTypeSection from "./sections/otherTypeSection";
//#endregion
import cssExports from "theme/styles/main.module.scss";

const MissingDocumentTodoSelectionForm: ForwardRefRenderFunction<
  FormHandles,
  MissingDocumentTodoSelectionFormViewProps
> = (props, ref) => {
  const {
    missingDocument,
    defaultValues,
    validationSchema,
    onFormValidationChange,
    onFormSubmitSuccess: onSubmitSuccess,
    hasReceiptDimensions,
    requiredReceiptDimensions,
    hasIncomingInvoiceDimensions,
    onFilesUploadChange,
    fileViewer,
    oldAttachments,
  } = props;

  const { wintFormProps, connect, values, formState } = useWintForm<MissingDocumentFormTypes.Todo>({
    formRef: ref,
    handleSubmit: props.handleSubmit,
    defaultValues: defaultValues,
    validationSchema: validationSchema,
    onFormValidationChange: onFormValidationChange,
    onSubmitSuccess: onSubmitSuccess,
  });

  const expandableStepProps = useWintExpandableStep(formState);

  const hasMatchedDocument = missingDocument.hasMatchedDocument ?? false;
  const { amount, date } = missingDocument;

  return (
    <WintForm {...wintFormProps}>
      <Grid container className={cssExports.sectionedForm}>
        <Grid container rowGap={1}>
          <TodoFileUploaderSection
            values={values}
            connect={connect}
            onFilesUploadChange={onFilesUploadChange}
            oldAttachments={oldAttachments}
          />
          <TodoPostWithoutPictureSection
            values={values}
            connect={connect}
            hasMatchedDocument={hasMatchedDocument}
            amount={amount}
          />
        </Grid>
        <TodoDocumentTypeSelectionStep
          values={values}
          connect={connect}
          hasMatchedDocument={hasMatchedDocument}
          amount={amount}
          date={missingDocument.date}
        />
        {values.type === WintSuperkollAPIModel.MissingDocumentPutType.Receipt && (
          <TodoReceiptTypeSection
            connect={connect}
            values={values}
            amount={amount}
            dimensionProps={{ hasDimensions: hasReceiptDimensions, required: requiredReceiptDimensions }}
            expandableStepProps={expandableStepProps}
            fileViewer={fileViewer}
          />
        )}
        {values.type === ApiModel.MissingDocumentPutType.IncomingInvoice && (
          <TodoIncomingInvoiceTypeSection
            connect={connect}
            values={values}
            amount={amount}
            date={date}
            hasDimensions={hasIncomingInvoiceDimensions}
            expandableStepProps={expandableStepProps}
            fileViewer={fileViewer}
          />
        )}
        {values.type === ApiModel.MissingDocumentPutType.Other && (
          <TodoOtherTypeSection
            connect={connect}
            amount={amount}
            hasDimensions={hasIncomingInvoiceDimensions || hasReceiptDimensions}
          />
        )}
      </Grid>
    </WintForm>
  );
};

export default forwardRef(MissingDocumentTodoSelectionForm);
